import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["loading", "content", "error"]
  static values = { url: String }

  connect() {
    this.load()
  }

  async load() {
    this.showLoading()

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload.error || "Request failed")
      }

      this.render(payload)
    } catch (error) {
      this.showError(error.message)
    }
  }

  render(payload) {
    const labels = payload.labels
    const pricingBlock = payload.mixed_types
      ? `<p class="m-0 mt-1">${this.escape(labels.mixed_types_note)}</p>`
      : `<p class="m-0 mt-1">${this.escape(labels.monthly_fee.replace("%{price}", payload.monthly_price_label).replace("%{number_type}", payload.number_type_label || ""))}</p>`

    const numbers = payload.numbers.length
      ? this.formHtml(payload)
      : `<p class="m-0 text-sm text-muted">${this.escape(labels.empty)}</p>`

    this.contentTarget.innerHTML = `
      <div class="rounded-lg border border-border bg-surface px-4 py-3 text-sm text-ink">
        <p class="m-0 font-semibold">${this.escape(labels.pricing_title)}</p>
        ${pricingBlock}
        <p class="m-0 mt-1 text-muted">${this.escape(labels.usage_note)}</p>
      </div>
      ${numbers}
    `

    this.hideLoading()
    this.contentTarget.hidden = false
  }

  formHtml(payload) {
    const labels = payload.labels
    const options = payload.numbers.map((number, index) => {
      const location = number.location
        ? `<span class="mt-0.5 block text-xs text-muted">${this.escape(number.location)}</span>`
        : ""

      return `
        <label class="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-white px-4 py-3 transition-colors has-[:checked]:border-primary/40 has-[:checked]:bg-primary/[0.03]">
          <input type="radio" name="phone_number" value="${this.escape(number.phone_number)}" class="mt-1" ${index === 0 ? "required" : ""}>
          <span class="min-w-0 flex-1">
            <span class="block font-mono text-sm font-semibold text-ink">${this.escape(number.phone_number)}</span>
            ${location}
            <span class="mt-1 block text-xs text-muted">
              ${this.escape(number.number_type_label)} · ${this.escape(number.price_label)}
            </span>
          </span>
        </label>
      `
    }).join("")

    return `
      <form action="${this.escape(payload.form.action)}" method="post" class="space-y-4">
        <input type="hidden" name="authenticity_token" value="${this.csrfToken}">
        <input type="hidden" name="country" value="${this.escape(payload.form.country)}">
        <fieldset class="m-0 space-y-2 border-0 p-0">
          <legend class="mb-2 text-sm font-bold text-ink">${this.escape(labels.choose)}</legend>
          ${options}
        </fieldset>
        <div class="flex flex-wrap items-center gap-3">
          <button type="submit" class="pro-btn pro-btn--primary">${this.escape(labels.purchase_cta)}</button>
          <a href="${this.escape(payload.form.back_url)}" class="pro-btn pro-btn--secondary">${this.escape(labels.back)}</a>
        </div>
      </form>
    `
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = false
    if (this.hasContentTarget) this.contentTarget.hidden = true
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true
  }

  showError(message) {
    this.hideLoading()
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  escape(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
  }
}
